import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from '../services/authApi'
import { AUTH_EVENT } from '../services/bff'

export const ME_KEY = ['auth', 'me']

// The one source of truth for "who is using this app". Every other auth-aware
// component reads this query rather than keeping its own copy.
//
// `auth: 'off'` (the VERALIS_AUTH=off dev switch) is reported by the server and
// makes the whole layer transparent — no login screen, no gate.
export function useAuth() {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: api.getMe,
    // Auth state must never be served stale after a 401: a revoked session that
    // still looks valid in cache would render the app around a ghost user.
    staleTime: 0,
    retry: false,
  })

  // A 401/forced-change anywhere in the app re-probes /me, which flips the gate.
  useEffect(() => {
    const onChange = () => qc.invalidateQueries({ queryKey: ME_KEY })
    window.addEventListener(AUTH_EVENT, onChange)
    return () => window.removeEventListener(AUTH_EVENT, onChange)
  }, [qc])

  const data = query.data
  return {
    loading: query.isLoading,
    enabled: data ? data.auth === 'on' : false,
    user: data?.user ?? null,
    setupRequired: !!data?.setup_required,
    mustChangePassword: !!data?.user?.must_change_password,
    isAdmin: data?.user?.role === 'admin',
    refetch: query.refetch,
  }
}

// Mutations all end by re-probing /me so the gate reacts in one place.
const withRefresh = (qc, fn) => async (...args) => {
  const out = await fn(...args)
  await qc.invalidateQueries({ queryKey: ME_KEY })
  return out
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: withRefresh(qc, ({ username, password }) => api.login(username, password)) })
}

export function useSetup() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: withRefresh(qc, api.setup) })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.logout()
      // Everything cached was fetched as the previous user; on a shared-data
      // instance that is not a leak, but it would flash the old content behind
      // the login screen. Drop it.
      qc.clear()
    },
  })
}

export function useChangePassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: withRefresh(qc, ({ current_password, new_password }) =>
      api.changePassword(current_password, new_password)
    ),
  })
}
